import { useEffect, useState } from "react";
import { FileText, Save, RotateCcw, Mail } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// LegalSettingsPanel — édition centralisée des mentions légales,
// directeur de publication, hébergeur, contact RGPD.
// Toutes les valeurs alimentent automatiquement les pages publiques
// /mentions-legales et /confidentialite.
// ─────────────────────────────────────────────────────────────

const FIELDS = [
    // Société exploitante
    { k: "brand_name", l: "Marque (XV Capital Partners)", group: "brand" },
    { k: "company_name", l: "Société exploitante", group: "brand" },
    { k: "company_short", l: "Sigle court", group: "brand" },
    { k: "legal_form", l: "Forme juridique", group: "brand" },
    { k: "siren", l: "SIREN", group: "brand" },
    { k: "siret", l: "SIRET du siège", group: "brand" },
    { k: "ape_code_internal", l: "Code APE (interne, non public)", group: "brand", internal: true },
    { k: "address_street", l: "Adresse", group: "address" },
    { k: "address_zip", l: "Code postal", group: "address" },
    { k: "address_city", l: "Ville", group: "address" },
    { k: "address_country", l: "Pays", group: "address" },
    { k: "created_at_legal", l: "Date de création", group: "address" },
    { k: "category", l: "Catégorie d'entreprise", group: "address" },
    { k: "activity_description", l: "Description d'activité", group: "address", textarea: true },
    // Publication
    { k: "publication_director", l: "Directeur de la publication", group: "pub" },
    { k: "dpo_email", l: "E-mail RGPD / DPO", group: "pub", type: "email" },
    // Hébergeur
    { k: "host_name", l: "Nom de l'hébergeur", group: "host" },
    { k: "host_address", l: "Adresse de l'hébergeur", group: "host" },
    { k: "host_contact", l: "Contact de l'hébergeur (e-mail / tél.)", group: "host" },
];

const GROUPS = [
    { v: "brand", l: "Société exploitante" },
    { v: "address", l: "Siège social & activité" },
    { v: "pub", l: "Publication & contact RGPD" },
    { v: "host", l: "Hébergement (admin-éditable)" },
];

export default function LegalSettingsPanel() {
    const [legal, setLegal] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [requests, setRequests] = useState([]);

    useEffect(() => {
        cockpit
            .get("/cockpit/settings/legal")
            .then(({ data }) => setLegal(data.legal))
            .catch((e) => setErr(e?.response?.data?.detail || "Chargement impossible"));
        cockpit
            .get("/cockpit/settings/legal/rgpd-requests")
            .then(({ data }) => setRequests(data.items || []))
            .catch(() => {});
    }, []);

    const change = (k) => (e) =>
        setLegal((l) => ({ ...l, [k]: e.target.value }));

    const save = async () => {
        if (!legal) return;
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const payload = { ...legal };
            delete payload.updated_at;
            delete payload.updated_by;
            const { data } = await cockpit.put(
                "/cockpit/settings/legal",
                payload,
            );
            setLegal(data.legal);
            setMsg("Mentions légales enregistrées.");
            setTimeout(() => setMsg(""), 3000);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="legal-settings-panel"
            className="border-t border-white/[0.08] pt-12 mt-12"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-7">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                        <FileText strokeWidth={1.1} size={11} />
                        Mentions légales & politique de confidentialité
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Données juridiques du site
                    </h2>
                    <p className="mt-2 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Toutes les valeurs ci-dessous alimentent
                        automatiquement les pages publiques /mentions-legales
                        et /confidentialite.
                    </p>
                </div>
            </div>

            {!legal ? (
                <p className="text-[12.5px] text-[#f4ecd8]/40 italic font-light">
                    Chargement…
                </p>
            ) : (
                <div className="space-y-10">
                    {GROUPS.map((g) => (
                        <div key={g.v}>
                            <div className="overline text-[#d4af37]/55 mb-5">
                                {g.l}
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {FIELDS.filter((f) => f.group === g.v).map((f) => (
                                    <div
                                        key={f.k}
                                        data-testid={`legal-field-${f.k}`}
                                        className={f.textarea ? "sm:col-span-2 lg:col-span-3" : ""}
                                    >
                                        <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-2">
                                            {f.l}
                                            {f.internal && (
                                                <span className="ml-2 normal-case text-[#e89090]/70 italic tracking-normal text-[10px]">
                                                    non public
                                                </span>
                                            )}
                                        </label>
                                        {f.textarea ? (
                                            <textarea
                                                rows={3}
                                                value={legal[f.k] || ""}
                                                onChange={change(f.k)}
                                                className="w-full bg-transparent border border-white/[0.08] focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] leading-relaxed resize-y"
                                            />
                                        ) : (
                                            <input
                                                type={f.type || "text"}
                                                value={legal[f.k] || ""}
                                                onChange={change(f.k)}
                                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14.5px] font-serif text-[#f4ecd8]"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {err && (
                        <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                            {err}
                        </div>
                    )}
                    {msg && (
                        <div className="text-[12.5px] text-[#a3d39c] border-l-2 border-[#a3d39c]/60 pl-4 py-1">
                            {msg}
                        </div>
                    )}

                    <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                        <span className="text-[10.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/40">
                            {legal.updated_at
                                ? `Dernière mise à jour : ${new Date(legal.updated_at).toLocaleString("fr-FR")}`
                                : "Aucune modification enregistrée"}
                        </span>
                        <button
                            type="button"
                            onClick={save}
                            disabled={busy}
                            data-testid="legal-save"
                            className="inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                        >
                            {busy ? <RotateCcw className="animate-spin" size={13} /> : <Save strokeWidth={1.2} size={13} />}
                            {busy ? "Enregistrement…" : "Enregistrer"}
                        </button>
                    </div>
                </div>
            )}

            {/* RGPD requests */}
            <div className="mt-12">
                <div className="overline text-[#d4af37]/55 mb-5 flex items-center gap-2">
                    <Mail strokeWidth={1.1} size={11} />
                    Demandes RGPD reçues
                    <span className="ml-2 normal-case tracking-normal text-[#f4ecd8]/45 text-[11px]">
                        ({requests.length})
                    </span>
                </div>
                {requests.length === 0 ? (
                    <p className="text-[13px] text-[#f4ecd8]/40 italic font-light">
                        Aucune demande reçue à ce jour.
                    </p>
                ) : (
                    <ul
                        data-testid="legal-rgpd-list"
                        className="border border-white/[0.06] divide-y divide-white/[0.04]"
                    >
                        {requests.slice(0, 20).map((r) => (
                            <li
                                key={r.id}
                                data-testid={`legal-rgpd-${r.id}`}
                                className="px-5 py-4"
                            >
                                <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
                                    <div>
                                        <span className="font-serif text-[14.5px] text-[#f4ecd8]">
                                            {r.full_name}
                                        </span>
                                        <span className="ml-3 text-[11px] text-[#f4ecd8]/45">
                                            &lt;{r.email}&gt;
                                        </span>
                                    </div>
                                    <span className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/75">
                                        {r.request_type}
                                    </span>
                                </div>
                                <p className="text-[13px] text-[#f4ecd8]/65 italic font-serif leading-relaxed">
                                    {r.message}
                                </p>
                                <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/35">
                                    {new Date(r.created_at).toLocaleString("fr-FR")} · {r.status}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
